import React, { useState, useId } from 'react';
import { UploadCloud, FileText, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * @file Componente de input de arquivo estilizado com funcionalidade de arrastar e soltar (drag-and-drop).
 * Oferece uma área interativa para upload de arquivos, com feedback visual e opção de remover arquivos.
 *
 * @param {object} props - As propriedades do componente.
 * @param {string} props.label - O rótulo exibido acima do campo de input.
 * @param {function} props.onChange - A função de callback executada quando a lista de arquivos muda.
 * @param {string} props.accept - Os tipos de arquivo aceitos (ex: ".pdf, .csv").
 * @param {boolean} [props.isMultiple=false] - Se permite a seleção de múltiplos arquivos.
 * @param {Array<File>} [props.files=[]] - Uma lista dos arquivos já selecionados para exibir.
 * @param {boolean} [props.disabled=false] - Se o campo deve estar desabilitado.
 */
const StyledFileInput = ({ label, onChange, accept, isMultiple = false, files = [], disabled = false }) => {
    // Estado para controlar se o usuário está arrastando um arquivo sobre o componente.
    const [isDragging, setIsDragging] = useState(false);
    // Gera um ID único para o input de arquivo, garantindo acessibilidade.
    const inputId = useId();

    // Previne o comportamento padrão do navegador para os eventos de arrastar.
    const preventDefaults = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    // --- LÓGICA DE MANIPULAÇÃO DE ARQUIVOS ---
    // Esta função centraliza a lógica de adicionar novos arquivos.
    const handleNewFiles = (newFilesList) => {
        if (!newFilesList || newFilesList.length === 0) return;

        // Se isMultiple for verdadeiro, combina os arquivos existentes com os novos.
        // Caso contrário, substitui a lista com os novos arquivos.
        const updatedFiles = isMultiple
            ? [...files, ...Array.from(newFilesList)]
            : Array.from(newFilesList);
        
        // Cria um evento sintético para passar ao componente pai, mantendo a consistência.
        const syntheticEvent = {
            target: {
                files: updatedFiles,
            },
        };
        onChange(syntheticEvent);
    };

    // --- CORREÇÃO APLICADA AQUI ---
    // A lógica de remoção agora é interna ao componente.
    const handleRemove = (indexToRemove) => {
        // Filtra a lista de arquivos para remover o item no índice especificado.
        const updatedFiles = Array.from(files).filter((_, index) => index !== indexToRemove);

        // Cria um evento sintético para notificar o componente pai sobre a mudança na lista de arquivos.
        const syntheticEvent = {
            target: {
                files: updatedFiles,
            },
        };
        onChange(syntheticEvent);
    };

    // Lida com o evento de arrastar um arquivo para dentro da área.
    const handleDragEnter = (e) => {
        preventDefaults(e);
        if (!disabled) {
            setIsDragging(true);
        }
    };

    // Lida com o evento de arrastar um arquivo para fora da área.
    const handleDragLeave = (e) => {
        preventDefaults(e);
        setIsDragging(false);
    };

    // Lida com o evento de soltar um arquivo na área.
    const handleDrop = (e) => {
        preventDefaults(e);
        if (disabled) return;
        setIsDragging(false);
        handleNewFiles(e.dataTransfer.files);
    };

    // Lida com a seleção de arquivos através do input nativo.
    const handleNativeInputChange = (e) => {
        handleNewFiles(e.target.files);
        // Reseta o valor do input para permitir que o mesmo arquivo seja selecionado novamente.
        e.target.value = null;
    };


    // Define as classes de CSS dinamicamente com base no estado (arrastando, desabilitado).
    const dropzoneClasses = `
        mt-1 flex justify-center px-6 py-10 border-2 border-dashed rounded-lg transition-colors duration-200
        ${disabled ? 'bg-slate-100 dark:bg-slate-800 cursor-not-allowed border-slate-200 dark:border-slate-700' : ''}
        ${isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-300 dark:border-slate-600'}
    `;

    return (
        <div>
            <label className={`block text-sm font-medium mb-1 ${disabled ? 'text-slate-400 dark:text-slate-500' : 'text-slate-600 dark:text-slate-400'}`}>{label}</label>
            <div
                className={dropzoneClasses}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={preventDefaults} // `onDragOver` também precisa prevenir o padrão.
                onDrop={handleDrop}
            >
                <div className="space-y-1 text-center">
                    <UploadCloud className={`mx-auto h-12 w-12 ${isDragging ? 'text-blue-500' : (disabled ? 'text-slate-300 dark:text-slate-600' : 'text-slate-400')}`} />
                    <div className="flex text-sm text-slate-600 dark:text-slate-400">
                        <label htmlFor={inputId} className={`relative rounded-md font-medium ${disabled ? 'text-slate-400 dark:text-slate-500 cursor-not-allowed' : 'text-blue-600 dark:text-blue-400 hover:text-blue-500 cursor-pointer focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500'}`}>
                            <span>Selecione o(s) arquivo(s)</span>
                            <input id={inputId} name={inputId} type="file" className="sr-only" accept={accept} multiple={isMultiple} onChange={handleNativeInputChange} disabled={disabled} />
                        </label>
                        <p className="pl-1">ou arraste e solte aqui</p>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        {isDragging ? "Solte para adicionar!" : (accept ? `Tipos aceitos: ${accept}` : 'Qualquer tipo de arquivo')}
                    </p>
                </div>
            </div>
            {/* Exibe a lista de arquivos selecionados */}
            {files.length > 0 && (
                <div className="mt-4">
                    <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{files.length} arquivo(s) selecionado(s):</h4>
                    <ul className="mt-2 flex flex-col gap-1" aria-live="polite">
                        <AnimatePresence initial={false} mode="popLayout">
                            {Array.from(files).map((file, index) => (
                                <motion.li
                                    layout
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
                                    key={`${file.name}-${file.size || index}`}
                                    className="flex items-center justify-between text-sm bg-slate-50 dark:bg-slate-800 p-2 rounded-md"
                                >
                                    <div className="flex items-center min-w-0"> {/* Container para truncar o texto */}
                                        <FileText className="h-4 w-4 mr-2 text-slate-500 flex-shrink-0" />
                                        <span className="truncate text-slate-700 dark:text-slate-300" title={file.name}>
                                            {file.name}
                                        </span>
                                    </div>
                                    {/* Botão para remover o arquivo da lista */}
                                    {!disabled && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemove(index)}
                                            className="p-1 ml-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full transition-colors"
                                            title="Remover arquivo"
                                            aria-label={`Remover ${file.name}`}
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                </motion.li>
                            ))}
                        </AnimatePresence>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default StyledFileInput;
